
export default class VideoRecorder {
  constructor(opts = {}) {
    // opts: { root: HTMLElement, question?: string, onStatus?:fn, onSentiments?:fn, onTranscript?:fn, onRecordingChange?:fn }
    this.root = opts.root || (typeof document !== 'undefined' ? document : null);
    this.question = opts.question || '';
    this.callbacks = {
      onStatus: opts.onStatus,
      onSentiments: opts.onSentiments,
      onTranscript: opts.onTranscript,
      onRecordingChange: opts.onRecordingChange,
    };

    this.mediaRecorder = null;
    this.videoChunks = [];
    this.isRecording = false;
    this.startTime = null;
    this.timerInterval = null;
    this.videoBlob = null;
    this.recordingDisabled = false;

    const q = (sel) => document.querySelector(sel);

    this.videoRecorder = q('#videoRecorder');
    this.videoFileUploader = q('#videoFileUploader');
    this.videoRecorderContainerNav = q('#videoRecorderContainerNav');
    this.openNav = q('#openNav');
    this.recordBtn = q('#recordBtn');
    this.rerecordBtn = q('#rerecordBtn');
    this.timer = q('#timer');
    this.videoPreview = q('#videoPreview');
    this.videoPlayback = q('#videoPlayback');
    this.uploadSection = q('#uploadSection');
    this.uploadBtn = q('#uploadBtn');
    this.fileName = q('#fileName');
    this.showStatus = q('#showStatus');
    this.statusMessage = q('#statusMessage');
    this.transcripts = q('#transcripts');
    this.sentiments = q('#sentiments');
    this.livePreview = q('#livePreview');
    this.answerQuestion = q('#answerQuestion');
    this.results = q('#results');
    this.answerQuestion.style.display='block';
    this.results.style.display='none';

    this.boundHandlers = {
      recordClick: () => {
        if (this.isRecording) {
          this.stopRecording();
        } else {
          this.startRecording();
        }
      },
      rerecordClick: () => this.rerecord(),
      uploadClick: () => this.uploadVideo(),
      openNavClick: () => this.showUploadSection(),
    };

    this.init();
  }

  async init() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        },
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        }
      });
      this.setupMediaRecorder(stream);
      this.bindEvents();
    } catch (error) {
      this.showStatusMessage('Camera/microphone access denied or not available: ' + error.message, 'error');
      console.error('Media device error:', error);
    }
  }

  setupMediaRecorder(stream) {
    this.stream = stream;
    // Attach live preview if available
    if (this.livePreview && 'srcObject' in this.livePreview) {
      try {
        this.livePreview.srcObject = stream;
        this.livePreview.muted = true;
        const playPromise = this.livePreview.play?.();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch (_) {}
    }
    // Use video/webm MIME type for better compatibility
    const options = { mimeType: 'video/webm;codecs=vp9,opus' };
    try {
      this.mediaRecorder = new MediaRecorder(stream, options);
    } catch (e) {
      console.error('MediaRecorder error:', e);
      // Fallback to default options
      this.mediaRecorder = new MediaRecorder(stream);
    }
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.videoChunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      this.videoBlob = new Blob(this.videoChunks, { type: 'video/webm' });
      if (this._rerecordPending) {
        // Skip preview/upload and immediately start a new recording
        this._rerecordPending = false;
        this.videoChunks = [];
        this.videoBlob = null;
        this.clearPreview?.();
        if (!this.recordingDisabled) {
          this.startRecording();
        } else {
          this.showStatusMessage('Recording is disabled while the left video is playing.', 'info');
        }
        return;
      }
      this.createVideoPreview();
      this.videoChunks = [];
    };
  }

  bindEvents() {
    if (this.recordBtn) {
      this.recordBtn.addEventListener('click', this.boundHandlers.recordClick);
    }
    if (this.rerecordBtn) {
      this.rerecordBtn.addEventListener('click', this.boundHandlers.rerecordClick);
    }
    if (this.uploadBtn) {
      this.uploadBtn.addEventListener('click', this.boundHandlers.uploadClick);
    }
    if (this.openNav) {
      this.openNav.addEventListener('click', this.boundHandlers.openNavClick);
    }
  }

  setRecordDisabled(disabled) {
    this.recordingDisabled = !!disabled;
    if (this.recordBtn) {
      this.recordBtn.disabled = !!disabled;
      this.recordBtn.title = disabled ? 'Recording disabled while the left video is playing' : '';
    }
    if (this.rerecordBtn) {
      this.rerecordBtn.disabled = !!disabled;
      this.rerecordBtn.title = disabled ? 'Recording disabled while the left video is playing' : '';
    }
  }

  startRecording() {
    if (this.recordingDisabled) {
      this.showStatusMessage('Recording is disabled while the left video is playing.', 'info');
      return;
    }
    this.videoChunks = [];
    this.mediaRecorder.start(1000); // Collect data every second
    this.isRecording = true;
    if (typeof this.callbacks.onRecordingChange === 'function') {
      try { this.callbacks.onRecordingChange(true); } catch (_) {}
    }
    this.startTime = Date.now();

    if (this.recordBtn) {
      this.recordBtn.textContent = '■ Stop';
      this.recordBtn.classList.add('recording');
    }

    //this.startTimer();
  }

  stopRecording() {
    this.mediaRecorder.stop();
    this.isRecording = false;
    if (typeof this.callbacks.onRecordingChange === 'function') {
      try { this.callbacks.onRecordingChange(false); } catch (_) {}
    }

    if (this.recordBtn) {
      this.recordBtn.textContent = '🎥 Start Over';
      this.recordBtn.classList.remove('recording');
      this.recordBtn.classList.add('stopped');
    }

    this.stopTimer();

    if (this.recordBtn) {
      setTimeout(() => {
        this.recordBtn.classList.remove('stopped');
      }, 1000);
    }
  }

  startTimer() {
    this.timerInterval = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      this.timer.textContent =
        `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }, 1000);
  }

  stopTimer() {
    clearInterval(this.timerInterval);
  }

  createVideoPreview() {
    const videoUrl = URL.createObjectURL(this.videoBlob);
    // this.videoPlayback.src = videoUrl;
    this.videoPreview.style.display = 'flex';
    const link = document.querySelector('#replayLink')
    link.href= videoUrl;
    link.target = '_blank';

    this.videoPreview.appendChild(link);
    //this.uploadSection.style.display = 'block';

    // Generate default filename
    const now = new Date();
    const timestamp = now.getFullYear() +
      (now.getMonth() + 1).toString().padStart(2, '0') +
      now.getDate().toString().padStart(2, '0') + '_' +
      now.getHours().toString().padStart(2, '0') +
      now.getMinutes().toString().padStart(2, '0');
    this.fileName.value = `video_${timestamp}`;

  }

  clearPreview() {
    if (this.videoPreview) {
      this.videoPreview.style.display = 'none';
      // Restore header
      this.videoPreview.innerHTML = '<h3>Recording Preview:</h3>';
    }
    /*
    if (this.uploadSection) {
      this.uploadSection.style.display = 'none';
    }*/
    if (this.fileName) {
      this.fileName.value = '';
    }
  }

  rerecord() {
    if (this.recordingDisabled) {
      this.showStatusMessage('Recording is disabled while the left video is playing.', 'info');
      return;
    }
    this.clearPreview?.();
    this.videoBlob = null;
    if (this.isRecording) {
      this._rerecordPending = true;
      try { this.stopRecording(); } catch (_) { this._rerecordPending = false; }
    } else {
      this.startRecording();
    }
  }

  showUploadSection() {
    if (this.videoRecorder) this.videoRecorder.style.display = 'flex';
    if (this.videoFileUploader) this.videoFileUploader.style.display = 'flex';
    if (this.videoRecorderContainerNav) this.videoRecorderContainerNav.style.display = 'none';
    if (this.statusMessage) this.statusMessage.style.display = 'none';
  }

  hideUploadSection() {

    this.answerQuestion.style.display='none';
    this.results.style.display='block';
    /*
    if (this.videoRecorder) this.videoRecorder.style.display = 'none';
    if (this.videoFileUploader) this.videoFileUploader.style.display = 'none';
    if (this.videoRecorderContainerNav) this.videoRecorderContainerNav.style.display = 'block';
    if (this.statusMessage) {
      this.statusMessage.style.display = 'block';
      this.statusMessage.style.width = '90%';
    }*/
  }

  async uploadVideo() {
    const fileName = this.fileName ? this.fileName.value.trim() : '';
    if (!fileName) {
      this.showStatusMessage('Please enter a filename', 'error');
      return;
    }

    if (!this.videoBlob) {
      this.showStatusMessage('No video recording to upload', 'error');
      return;
    }

    const formatBytes = (bytes) => {
      if (bytes === 0 || !Number.isFinite(bytes)) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      const val = bytes / Math.pow(k, i);
      return `${val.toFixed(i === 0 ? 0 : 2)} ${sizes[i]}`;
    };

    const totalSize = this.videoBlob?.size || 0;
    /*const sizeMsg = `Preparing upload: file size ${formatBytes(totalSize)} (${totalSize.toLocaleString()} bytes)`;
    this.showStatusMessage(sizeMsg, 'info');
*/
    if (this.uploadBtn) {
      this.uploadBtn.disabled = true;
      this.uploadBtn.textContent = '↑ Uploading...';
    }

    const formData = new FormData();
    formData.append('video', this.videoBlob, fileName + '.webm');
    formData.append('fileName', fileName);
    formData.append('question', this.question || '');

    // Use XMLHttpRequest to get reliable upload progress events
    const url = '/wp-json/sentiment/v1/handle_video_upload';
    await new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.responseType = 'text';
      xhr.timeout = 120000; // 2 minutes

      const startTs = Date.now();
      let lastLoaded = 0;
      xhr.upload.onprogress = (e) => {
        const loaded = e.loaded ?? 0;
        const total = e.total || totalSize || 0;
        const percent = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
        const now = Date.now();
        const elapsedSec = Math.max(0.001, (now - startTs) / 1000);
        const speed = loaded / elapsedSec; // bytes/sec
        const remaining = total ? Math.max(0, total - loaded) : 0;
        const etaSec = speed > 0 ? Math.round(remaining / speed) : 0;

        // Prevent flooding DOM updates if no progress
        if (loaded === lastLoaded && loaded !== 0) return;
        lastLoaded = loaded;

        if (this.showStatus) {
          this.showStatus.innerHTML = `
            <div class="status-info">
              <div>Uploading ${formatBytes(totalSize)} — ${percent}%</div>
              <div>${formatBytes(loaded)} of ${formatBytes(total)} @ ${formatBytes(speed)}/s ${etaSec ? `(ETA ${etaSec}s)` : ''}</div>
              <div style="height:8px;background:#e2e8f0;border-radius:4px;margin-top:6px;">
                <div style="width:${percent}%;height:100%;background:#3182ce;border-radius:4px;"></div>
              </div>
            </div>`;
        }
      };

      xhr.onload = () => {
        let json = null;
        try {
          json = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch (_) {}

        if (xhr.status === 413) {
          const human = formatBytes(totalSize);
          this.showStatusMessage(`❌ 413 Payload Too Large. File size ${human}. Your server may limit uploads to ~2MB. See notes below.`, 'error');
          resolve();
          return;
        }
        if (xhr.status >= 400) {
          const msg = (json && (json.message || json.data)) || `HTTP ${xhr.status}`;
          this.showStatusMessage('❌ Error: ' + msg, 'error');
          resolve();
          return;
        }

        // First step complete: expect a URI from handle_video_upload
        // Server may return either JSON with success or a plain string URI
        let uri = null;
        if (json && typeof json === 'object' && json.success) {
          const data = json.data || {};
          uri = data.uri || data.url || json.uri || json.url || (typeof data === 'string' ? data : null);
        } else if (typeof json === 'string' && json.trim()) {
          uri = json.trim();
        } else if (typeof xhr.responseText === 'string' && xhr.responseText.trim()) {
          // Fallback: raw text body is the URI (strip wrapping quotes if any)
          const raw = xhr.responseText.trim();
          uri = raw.replace(/^"|"$/g, '');
        }

        if (uri) {
          // Update UI to indicate analysis phase
          if (this.uploadBtn) {
            this.uploadBtn.textContent = '↺ Analyzing…';
          }
          if (this.showStatus) {
            this.showStatus.innerHTML = `
              <div class="status-info">
                <div>Upload complete. Analyzing your response…</div>
              </div>`;
          }

          // Second step: call handle_video_analysis with the returned URI
          const analyze = () => new Promise((resolve2) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutes

            const fd2 = new FormData();
            fd2.append('uri', uri);
            fd2.append('fileName', fileName);
            fd2.append('question', this.question || '');

            fetch('/wp-json/sentiment/v1/handle_video_analysis', {
              method: 'POST',
              body: fd2,
              signal: controller.signal,
            })
              .then(async (res) => {
                const text = await res.text();
                let json2 = null;
                try { json2 = text ? JSON.parse(text) : null; } catch (_) {}

                if (!res.ok) {
                  const msg2 = (json2 && (json2.message || json2.data)) || `HTTP ${res.status}`;
                  this.showStatusMessage('❌ Analysis error: ' + msg2, 'error');
                  return;
                }

                if (json2 && json2.success) {
                  this.showSentiments(json2.data);
                  setTimeout(() => this.hideUploadSection(), 1000);
                } else {
                  const msg2 = (json2 && (json2.message || json2.data)) || 'Analysis finished but response was not recognized';
                  this.showStatusMessage('⚠ ' + msg2, 'error');
                }
              })
              .catch((err) => {
                if (err && err.name === 'AbortError') {
                  this.showStatusMessage('⚠ Analysis timed out. Please try again later.', 'error');
                } else {
                  this.showStatusMessage('⚠ Network error during analysis.', 'error');
                }
              })
              .finally(() => {
                clearTimeout(timeoutId);
                resolve2();
              });
          });

          analyze().then(() => resolve());
        } else {
          const msg = (json && (json.message || json.data)) || 'Upload finished but response was not recognized';
          this.showStatusMessage('⚠ ' + msg, 'error');
          resolve();
        }
      };

      xhr.onerror = () => {
        this.showStatusMessage('⚠ Network error during upload.', 'error');
        resolve();
      };
      xhr.ontimeout = () => {
        this.showStatusMessage('⚠ Upload timed out. Try a smaller file or check your connection.', 'error');
        resolve();
      };

      xhr.send(formData);
    });

    if (this.uploadBtn) {
      this.uploadBtn.disabled = false;
      this.uploadBtn.textContent = '↑ Submit';
    }
  }

  showStatusMessage(message, type) {
    if (this.showStatus) {
      this.showStatus.innerHTML = `<div class="status-${type}">${message}</div>`;
    }
    if (this.statusMessage) {
      this.statusMessage.textContent = message || '';
    }
    if (typeof this.callbacks.onStatus === 'function') {
      this.callbacks.onStatus({ message, type });
    }
  }

  showTranscript(transcript) {
    if (this.transcripts) {
      this.transcripts.innerHTML = '<div class="transcript"><h2>Transcript</h2>' + transcript + '</div>';
    }
    if (typeof this.callbacks.onTranscript === 'function') {
      this.callbacks.onTranscript(transcript);
    }
  }

  showSentiments(data) {
    if (this.sentiments) {
      const sentimentsHeader = '';//<b style="font-size:2rem">Analysis</b><div class="llms" >';
      const noSentimentHTML = '<div id="nosentiment">No analysis available.</div>';

      let geminiHTML = noSentimentHTML;

      if (data) {
        const toTitle = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

        const renderValue = (val, depth = 0) => {
          if (val === null || val === undefined) return '';
          const indent = ' ' .repeat(depth * 2);
          if (Array.isArray(val)) {
            if (val.length === 0) return '(none)';
            // If array of primitives
            if (val.every(v => (typeof v !== 'object' || v === null))) {
              return '<ul style="padding-left:25px; list-style-type:none">' + val.map(v => '<li>' + String(v) + '</li>').join('') + '</ul>';
            }
            // Array of objects
            return val.map((item, idx) => {
              if (typeof item === 'object' && item !== null) {
                const inner = Object.entries(item)
                  .map(([k, v]) => '<li><b margin-top:1.2rem; margin-bottom:-1rem;display:block;>' + toTitle(k) + ':</b> ' + (typeof v === 'object' ? renderValue(v, depth + 1) : String(v)) + '</li>')
                  .join('');

                return '<div><ul style="padding-left:25px; list-style-type:none; margin-top:-1rem; padding-top:.5rem; >' + inner + '</ul></div>';
              }
              return '<div>' + String(item) + '</div>';
            }).join('');
          }
          if (typeof val === 'object') {
            const inner = Object.entries(val)
              .map(([k, v]) => '<li><b margin-top:1.2rem; margin-bottom:-1rem;display:block;>' + toTitle(k) + ':</b> ' + (typeof v === 'object' ? renderValue(v, depth + 1) : String(v)) + '</li>')
              .join('');
            return '<ul  style="padding-left:25px; list-style-type:none">' + inner + '</ul>';
          }
          if (typeof val === 'boolean') return val ? 'true' : 'false';
          return String(val);
        };

        // Place score (if present) prominently on top
        if (typeof data.overall_score !== 'undefined') {
          geminiHTML = '<div style="font-size:28px"><b>Overall Score: </b> ' + data.overall_score + '</div>';
        } else {
          geminiHTML = '';
        }

        // Render all fields from the JSON with headers
        for (const [key, value] of Object.entries(data)) {
          const title = key ==='title' ? '' : toTitle(key);
          const displayValue = key ==='title' ? '<b>' + value + '</b>' : value;
          // Skip duplicate score line as it is already rendered above
          if (key === 'overall_score') continue;
          geminiHTML += '<div><b  style="font-size:1.2rem;font-weight:bold;margin-top:1.0rem;display:block;">' + title + ' :</b> ' + renderValue(value) + '</div>';
        }

        geminiHTML += '</div>';
      }

      this.sentiments.innerHTML = sentimentsHeader + geminiHTML + '</div>';
    }
    if (typeof this.callbacks.onSentiments === 'function') {
      this.callbacks.onSentiments(data);
    }
  }

  destroy() {
    // Unbind events
    if (this.recordBtn && this.boundHandlers?.recordClick) {
      this.recordBtn.removeEventListener('click', this.boundHandlers.recordClick);
    }
    if (this.rerecordBtn && this.boundHandlers?.rerecordClick) {
      this.rerecordBtn.removeEventListener('click', this.boundHandlers.rerecordClick);
    }
    if (this.uploadBtn && this.boundHandlers?.uploadClick) {
      this.uploadBtn.removeEventListener('click', this.boundHandlers.uploadClick);
    }
    if (this.openNav && this.boundHandlers?.openNavClick) {
      this.openNav.removeEventListener('click', this.boundHandlers.openNavClick);
    }

    // Stop recording if ongoing
    try {
      if (this.isRecording && this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    } catch (e) {
      // ignore
    }
    this.stopTimer?.();

    // Stop media tracks
    if (this.stream) {
      this.stream.getTracks?.().forEach(t => {
        try { t.stop(); } catch (_) {}
      });
    }
  }
}